import { isEmpty, isNil } from 'lodash'

/**
 * How much worse than its own current score a prompt may measure and still count as migrating, in percentage
 * points. `NO_THRESHOLD` takes every prompt that was measured at all, however far short it fell.
 */
export const NO_THRESHOLD = 'none'
export const TOLERANCE_OPTIONS = [0, 2, 3, 4, 5, NO_THRESHOLD]
export const RECOMMENDED_TOLERANCE = 2

/**
 * The two kinds of prompt a scorecard runs, as the server names them.
 *
 * A criterion is scored per call and has its own row in the config; the eligibility gate belongs to the
 * config itself and decides whether a call is scored at all. They are repointed differently when applying,
 * which is the one place the difference matters.
 */
export const KIND = {
  CRITERION: 'criterion',
  ELIGIBILITY: 'eligibility',
}

/**
 * The prompts a run would actually measure.
 *
 * The server lists EVERY prompt on the scorecard, flagging the ones already on the target model, so a
 * scorecard part-way through reads as one list. Those have nothing left to move: nothing to test, nothing to
 * apply, no place in the saving. Which model the target is stays the server's business — the page never
 * names it, so moving the target needs no frontend release.
 */
export const migratableSubjects = (subjects) => subjects.filter((subject) => !subject.onTarget)

/** What a scorecard has to migrate, over its criteria plus its eligibility prompt. */
export const scorecardOpportunity = (subjects) => {
  const migratable = migratableSubjects(subjects)

  return { nMigratable: migratable.length, fullyMigrated: isEmpty(migratable) }
}

export const formatPercent = (value) => (isNil(value) ? '—' : `${Math.round(value)}%`)

/**
 * How a subject and a result about it are matched to each other.
 *
 * ⚠ THE LINEAGE, falling back to the key. A key names one VERSION of a prompt and every scorecard save mints
 * new ones, so anything joining a result to a live prompt by key comes apart the moment the scorecard is
 * saved — which applying a migration does.
 */
const identity = (entry) => entry?.lineageKey ?? entry?.key

/**
 * A scorecard's prompts with whatever the run found for each, as one list.
 *
 * ⚠ MATCHED ON THE LINEAGE, not the key — see `identity`. Joined by key, one criterion appears twice after
 * an apply: a result saying "migrated" with nothing live beside it, and a live prompt with no result.
 *
 * ⚠ EVERY RESULT STILL GETS A ROW, even one whose prompt has left the scorecard. Deleting a criterion does
 * not make what we measured about it untrue, and a result carries its own name and kind for that case.
 *
 * `running` marks the prompts a run in flight is about. A whole-scorecard run names none, so an empty list
 * means all of them.
 */
export const subjectRows = (subjects, criteria = [], running = null) => {
  const byResult = new Map(criteria.map((criterion) => [identity(criterion), criterion]))
  const live = new Set(subjects.map(identity))
  // Which prompts a run in flight covers. A run naming none covers every row, since that is a whole-scorecard
  // run — so whether there is a run at all is a separate fact from the list, and both are needed.
  const hasRun = !isNil(running)
  const covered = running ?? []

  // Each row takes its key from the side it came from: a live prompt's own key, so testing or applying the
  // row names the version that exists now, and a departed prompt's result keeps its own.
  const pairs = [
    ...subjects.map((subject) => ({
      key: subject.key,
      subject,
      result: byResult.get(identity(subject)) ?? null,
    })),
    ...criteria
      .filter((criterion) => !live.has(identity(criterion)))
      .map((criterion) => ({ key: criterion.key, subject: null, result: criterion })),
  ]

  return pairs.map(({ key, subject, result }) => ({
    key,
    kind: subject?.kind ?? result?.kind ?? null,
    name: subject?.name ?? result?.name ?? null,
    model: subject?.model ?? result?.incumbentModel ?? null,
    result,
    // Already on the cheap model: the row shows, saying there is nothing to do, rather than offering a test
    // the server would refuse.
    onTarget: subject?.onTarget ?? false,
    migratable: !isNil(subject) && !subject.onTarget,
    running: hasRun && (isEmpty(covered) || covered.includes(key)),
  }))
}

/**
 * How long a run should take, as a RANGE — the quantity is variable enough that a point estimate is wrong by
 * about 2x either way.
 *
 * ⚠ It overstates a whole scorecard, whose prompts share the eligibility work. Overstating is the right
 * direction for something someone is deciding whether to wait for.
 *
 * The server sends both ends, since the policy that decides them is a flow parameter; these fallbacks cover
 * a server that is not sending them yet.
 */
export const DEFAULT_PER_PROMPT_MINUTES = { min: 15, max: 25 }

export const estimateRunMinutes = (nSubjects, perPrompt) => {
  const count = nSubjects || 1
  const min = perPrompt?.min ?? DEFAULT_PER_PROMPT_MINUTES.min
  const max = perPrompt?.max ?? DEFAULT_PER_PROMPT_MINUTES.max

  return { low: Math.max(1, Math.round(count * min)), high: Math.max(1, Math.round(count * max)) }
}

// Rounded, because an estimate that reads "1 hr 47 min" claims a precision it does not have.
const roundedHours = (minutes) => {
  const hours = minutes / 60

  return hours < 3 ? Math.round(hours * 2) / 2 : Math.round(hours)
}

/**
 * A range as one phrase — "15 to 25 minutes", "2 to 3.5 hours".
 *
 * The unit is named once, on the second number, so the reader is not asked to parse two units to compare two
 * numbers. Mixed units ("40 minutes to 1.5 hours") only when the range genuinely straddles the hour.
 */
export const formatDuration = ({ low, high }) => {
  if (high < 60) {
    // Collapsed when the two ends round to the same number — "7 to 7 hours" reads as a bug.
    return low === high ? `${high} minutes` : `${low} to ${high} minutes`
  }

  if (low < 60) {
    return `${low} minutes to ${roundedHours(high)} hours`
  }

  const [lowHours, highHours] = [roundedHours(low), roundedHours(high)]

  return lowHours === highHours ? `${highHours} hours` : `${lowHours} to ${highHours} hours`
}

/**
 * Every verdict the server sends, and the only list of them on this side.
 *
 * ⚠ The page has to tell an ANSWER from a non-answer: `incomplete` means a rung errored and
 * `not_measurable` that no bar was established, so neither is a finding about the prompt. Both still carry a
 * `marginPct` from whatever did run, so anything keying on the margin alone reads them as measurements.
 */
export const VERDICT = {
  MIGRATES: 'migrates',
  DOES_NOT_MIGRATE: 'does_not_migrate',
  INCOMPLETE: 'incomplete',
  NOT_MEASURABLE: 'not_measurable',
}

const ANSWERED = [VERDICT.MIGRATES, VERDICT.DOES_NOT_MIGRATE]

/**
 * The two attempts a prompt gets: the same prompt on the cheap model, then a prompt rewritten for it.
 * `atRung` names whichever one held, and that decides what applying would publish — the prompt already
 * live, or the rewrite's own text.
 */
const RUNG = {
  SAME_PROMPT: 'same_prompt',
  NEW_PROMPT: 'new_prompt',
}

/**
 * Did the ladder finish and produce an answer about this prompt.
 *
 * Exported so the status column and the apply button ask the same question. An unrecognised verdict is NOT
 * an answer — it means the two sides have drifted apart, which is not a finding about the prompt.
 */
export const isAnswered = (criterion) => ANSWERED.includes(criterion?.verdict)

// A prompt with no margin was never measured — a refusal, or a score the run refused to count — so there is
// nothing to apply at any threshold. Optional, because a row with no result at all asks this too.
const wasMeasured = (criterion) => !isNil(criterion?.marginPct) && isAnswered(criterion)

/**
 * ⭐ WHICH RUNG'S PROMPT WOULD GO LIVE. `applyRung` where the server says, else the rung the verdict took.
 *
 * ⚠ These differ, and the difference is why the server has to say. `atRung` is read at a THRESHOLD, so a
 * result below the one being read has no winning rung — while the score on its row is the best any rung
 * measured, which is usually the rewrite. Publishing on `atRung` alone therefore keeps the live wording and
 * just moves the model: a plain model swap, live under a number the rewrite earned.
 */
const applyTarget = (criterion) => criterion?.applyRung ?? criterion?.atRung

/**
 * ⚠ A REWRITE WOULD GO LIVE AND ITS TEXT DID NOT ARRIVE, which cannot be applied.
 *
 * Applying overwrites the live prompt's wording with the rewrite's. With no rewrite to overwrite it with, it
 * would publish the wording already live onto the cheap model, under a result saying a rewrite was needed to
 * hold — and nothing on screen would show it. Exported so the status column can say so rather than leaving
 * the row looking merely below threshold.
 */
export const isMissingItsRewrite = (criterion) =>
  applyTarget(criterion) === RUNG.NEW_PROMPT && isNil(criterion?.newPrompt)

/**
 * Does this result say which prompt applying it would publish.
 *
 * Fails closed. `same_prompt` publishes the wording already live, which needs no text; `new_prompt` needs
 * its rewrite. Anything else — a result read below the threshold by a server that does not send `applyRung`
 * — cannot be published, because nothing here knows what it would write.
 */
const namesItsApplyTarget = (criterion) =>
  applyTarget(criterion) === RUNG.SAME_PROMPT ||
  (applyTarget(criterion) === RUNG.NEW_PROMPT && !isNil(criterion?.newPrompt))

/** Is this prompt's measured margin inside the threshold the reader has chosen. */
const withinThreshold = (criterion, tolerancePct) =>
  tolerancePct === NO_THRESHOLD || criterion.marginPct >= -tolerancePct

/**
 * ⭐ CAN THIS RESULT BE PUBLISHED AT ALL — everything except the threshold, which is a judgement rather than
 * a property of the result.
 *
 * Refused here whatever anyone decides: a result that measured nothing (a refusal, or a score withheld for
 * dropping an evidence field), an unfinished ladder, a stale result — which would overwrite a newer edit —
 * a winning rewrite whose text never arrived, and one already applied, where `migrated` means what is live
 * IS this result's prompt.
 *
 * Separate from the threshold so one prompt can be published on its own: a reader who has looked into a
 * regression and accepted it needs a way to take that prompt without loosening the threshold for every
 * prompt beside it.
 */
export const canApply = (criterion) =>
  wasMeasured(criterion) &&
  !criterion.stale &&
  !criterion.migrated &&
  namesItsApplyTarget(criterion)

/**
 * Which criteria a threshold would apply — what `canApply` allows, inside the chosen allowance.
 *
 * The threshold is read from the stored margin rather than from the verdict the run recorded, so moving it
 * re-reads the same results with nothing re-run.
 */
export const criteriaPassingAt = (criteria, tolerancePct) =>
  criteria.filter((criterion) => canApply(criterion) && withinThreshold(criterion, tolerancePct))

/**
 * How much cheaper the cheap model ran this prompt, as a percentage of what the incumbent cost.
 *
 * Billed cost against billed cost — both sides ran on the same calls in the same run — rather than a
 * projection. Negative when the cheap model turned out more expensive, which does happen.
 */
export const costReductionPct = (criterion) => {
  const before = criterion?.incumbentCostPerCall
  const after = criterion?.newCostPerCall

  if (isNil(before) || isNil(after) || !before) {
    return null
  }

  return ((before - after) / before) * 100
}

/**
 * The same number as a change in cost: negative is cheaper, positive is more expensive.
 *
 * ⚠ Reported this way round because the cheap model is sometimes dearer, and a column headed "Cheaper by"
 * showing "-12%" asserts the opposite of what happened. A signed cost change reads correctly either way.
 */
export const costChangePct = (criterion) => {
  const reduction = costReductionPct(criterion)

  return isNil(reduction) ? null : -reduction
}

export const formatCostChange = (value) =>
  isNil(value) ? '—' : `${value > 0 ? '+' : ''}${Math.round(value)}%`

/**
 * How much cheaper THIS SCORECARD gets, as a percentage of what it costs to score a call today.
 *
 * ⚠ COST-WEIGHTED, not a mean of percentages. Prompts on one scorecard can differ fourfold in what they
 * cost per call, so averaging their reduction percentages answers "how much cheaper is the average prompt",
 * which is not the question. One prompt of ten getting 100% cheaper on a tenth of the bill reads as 10%.
 *
 * Prompts the threshold would not take stay at their current cost, which is what makes the number move with
 * the threshold. A prompt with no measured cost is left out of BOTH sides rather than counted as free.
 */
export const scorecardSavingPct = (subjects, criteria, tolerancePct) => {
  if (isEmpty(subjects)) {
    return null
  }

  // On the lineage, not the key: a saved scorecard leaves the live prompt and its result under different
  // keys, and a subject that cannot find its result is left out of the figure entirely.
  const byKey = new Map(criteria.map((criterion) => [identity(criterion), criterion]))
  const applying = criteriaPassingAt(criteria, tolerancePct)

  let before = 0
  let after = 0

  subjects.forEach((subject) => {
    const criterion = byKey.get(identity(subject))
    const now = criterion?.incumbentCostPerCall

    if (isNil(now)) {
      return
    }

    const migrated = criterion.newCostPerCall
    const takesIt = applying.includes(criterion) && !isNil(migrated)

    before += now
    after += takesIt ? migrated : now
  })

  return before ? ((before - after) / before) * 100 : null
}

/**
 * How many of a scorecard's prompts the saving above could actually price.
 *
 * ⚠ Fewer than all of them is normal on a partly tested scorecard, and the saving is then a percentage of a
 * smaller bill than the reader is looking at: unsaid, "39% cheaper" reads as the whole scorecard.
 */
export const nPricedSubjects = (subjects, criteria) => {
  const byKey = new Map(criteria.map((criterion) => [identity(criterion), criterion]))

  return subjects.filter((subject) => !isNil(byKey.get(identity(subject))?.incumbentCostPerCall))
    .length
}

/**
 * One criterion's per-call labels, for a prompt engineer who wants to go and look at the calls.
 *
 * One column per RUN, not per arm: the incumbent is re-run several times on its own model to establish the bar,
 * and the candidate is drawn more than once too, so collapsing each side to a single label hides the
 * disagreement that the whole comparison turns on. Column counts follow the data, since how many draws each
 * side gets is a policy setting.
 *
 * Rows of arrays rather than a string, because `downloadCsvTemplate` does the quoting.
 */
export const resultRows = (criterion) => {
  const width = (field) =>
    criterion.calls.reduce((widest, call) => Math.max(widest, call[field].length), 0)

  // Each side contributes its answers and, beside them, whether each draw matched the reference. The match
  // columns come from the scorer rather than from comparing the answers, so they are present even where the
  // answers are withheld — which is why they are columns rather than something the reader works out.
  const series = [
    { label: 'incumbent_rerun', answers: 'incumbentLabels', matched: 'incumbentMatched' },
    { label: 'same_prompt', answers: 'samePromptLabels', matched: 'samePromptMatched' },
    { label: 'new_prompt', answers: 'newPromptLabels', matched: 'newPromptMatched' },
  ].map((one) => ({ ...one, n: Math.max(width(one.answers), width(one.matched)) }))

  const columns = series.flatMap((one) =>
    Array.from({ length: one.n }, (_unused, index) => [
      { key: one.answers, index },
      { key: one.matched, index },
    ]).flat()
  )

  return [
    [
      'call_id',
      // ⚠ ONE COLUMN, not two. Production's own answer and the reference each draw was scored against are
      // the same value by construction — the reference IS production's stored answer, which is what makes a
      // passing score mean UNCHANGED rather than correct. A second column earns its place the day a
      // migration is scored against something else (`funnel.GT_METHOD` is a constant today).
      'production_answer',
      ...series.flatMap((one) =>
        Array.from({ length: one.n }, (_unused, index) => [
          `${one.label}_${index + 1}`,
          `${one.label}_${index + 1}_matched`,
        ]).flat()
      ),
    ],
    ...criterion.calls.map((call) => [
      call.callId,
      call.productionLabel ?? '',
      ...columns.map((column) => {
        const value = call[column.key][column.index]

        // A side drawn fewer times than another leaves a gap here, and so does a draw the scorer never
        // reached. Both are an empty cell rather than a shifted row.
        return isNil(value) ? '' : value
      }),
    ]),
  ]
}
